import capitalize from 'lodash/capitalize';
import * as Yup from 'yup';

import { formatDate, pm } from 'lib/helpers/utility';

import { MEMBER_OPTIONS } from 'components/shared/Filters/lib/filterOptions';

import Actions from '../components/Actions';

import { categories, priorities, statuses } from './constants';

const removeHtmlTags = (html) => {
  if (!html) return '';

  const formattedHtml = html.replace(/<br\s*\/?>/gi, ' ');

  return formattedHtml.replace(/<[^>]*>/g, '');
};

const matchElement = (value, key) => {
  const element = key.find((elem) => elem.value === value);
  return element && element.value;
};

const showLabel = (value, key) => {
  const element = key.find((elem) => elem.value === value);
  return element ? element.label : '-';
};

const showUsersFormat = (value, kind) => {
  const taskUsers = value
    ?.filter((taskUser) => taskUser?.user_kind === kind)
    .map((users) => users?.user?.map((data) => `${data?.f_name} ${data?.l_name}`));
  return taskUsers;
};

export const TaskValidationSchema = Yup.object().shape({
  task: Yup.object().shape({
    title: Yup.string().required('Title is required'),
    status: Yup.string().required('Status is required'),
    category: Yup.string().required('A category is required')
  }),

  users: Yup.object().shape({
    requester: Yup.string().required('A requester is required'),
    owners: Yup.array().min(1, 'At least one owner is required')
  })
});

export const taskInitialValues = (initialTask) => ({
  task: {
    title: initialTask?.title || '',
    description: initialTask?.description || '',
    category: matchElement(initialTask?.category, categories) || '',
    priority: matchElement(initialTask?.priority, priorities) || null,
    status: matchElement(initialTask?.status, statuses) || 'requested',
    due_date: initialTask?.due_date || new Date(),
    data: initialTask?.data || null
  },
  users: {
    requester:
      initialTask?.users?.filter((user) => user.user_kind === 'requester')[0]?.user_id || '',
    owners:
      initialTask?.users
        ?.filter((user) => user.user_kind === 'owner')
        .map((owner) => owner.user_id) || [],
    followers:
      initialTask?.users
        ?.filter((user) => user.user_kind === 'follower')
        .map((follower) => follower.user_id) || []
  }
});

export const shapeTaskValues = (values) => ({
  task: {
    title: values?.task?.title,
    description: values?.task?.description,
    category: values?.task?.category,
    priority: values?.task?.priority,
    status: values?.task?.status,
    due_date: values?.task?.due_date,
    data: values?.task?.data
  },
  users: {
    requester: values?.users?.requester,
    owners: values?.users?.owners,
    followers: values?.users?.followers
  }
});

export const defaultColumns = (timezone, permissions) => [
  {
    headerName: 'Title',
    cellClass: 'no-border',
    valueFormatter: ({ value }) => (value ? value : '-'),
    field: 'title',
    headerCheckboxSelection: true,
    checkboxSelection: (params) => !params.node.group
  },
  {
    field: 'description',
    headerName: 'Description',
    cellClass: 'no-border',
    valueFormatter: ({ value }) => (value ? capitalize(removeHtmlTags(value)) : '-')
  },
  {
    field: 'category',
    headerName: 'Category',
    cellClass: 'no-border',
    valueFormatter: ({ value }) => showLabel(value, categories)
  },
  {
    field: 'priority',
    headerName: 'Priority',
    cellClass: 'no-border',
    valueFormatter: ({ value }) => showLabel(value, priorities)
  },
  {
    field: 'requesters',
    headerName: 'Requester',
    cellClass: 'no-border',
    sortable: false,
    valueFormatter: (params) => showUsersFormat(params?.data?.users, 'requester')
  },
  {
    field: 'owners',
    headerName: 'Owners',
    cellClass: 'no-border',
    sortable: false,
    valueFormatter: (params) => showUsersFormat(params?.data?.users, 'owner')
  },
  {
    field: 'followers',
    headerName: 'Followers',
    cellClass: 'no-border',
    sortable: false,
    valueFormatter: (params) => showUsersFormat(params?.data?.users, 'follower')
  },
  {
    field: 'status',
    headerName: 'Status',
    cellClass: 'no-border',
    valueFormatter: ({ value }) => (value ? capitalize(value) : '-')
  },
  {
    field: 'due_date',
    headerName: 'Due Date',
    cellClass: 'no-border',
    valueFormatter: ({ value }) => (value ? formatDate(value, timezone) : '-')
  },
  {
    field: 'updated_at',
    headerName: 'Last Updated',
    cellClass: 'no-border',
    valueFormatter: ({ value }) => (value ? formatDate(value, timezone) : '-')
  },
  {
    field: 'created_at',
    headerName: 'Created On',
    cellClass: 'no-border',
    valueFormatter: ({ value }) => (value ? formatDate(value, timezone) : '-')
  },
  ...(pm(permissions, ['tasks.update', 'tasks.delete'], 'or')
    ? [
        {
          field: 'actions',
          headerName: 'Actions',
          cellClass: 'no-border',
          cellRenderer: Actions,
          maxWidth: 88,
          minWidth: 36,
          resizable: false,
          sortable: false,
          pinned: 'right'
        }
      ]
    : [])
];
export const DEFAULT_COLUMN_DEFS = { resizable: true, enableRowGroup: true };

export const DEFAULT_FILTERS = {
  due_date: {
    type: 'date-range',
    title: 'Due Date',
    icon: 'calendar-mono'
  },
  title: {
    title: 'Task name',
    allowed_operators: ['contains', 'does not contain'],
    icon: 'editor-text-mono'
  },
  description: {
    title: 'Task description',
    allowed_operators: ['contains', 'does not contain'],
    icon: 'editor-text-mono'
  },
  requester: { ...MEMBER_OPTIONS, title: 'Requesters' },
  owner: { ...MEMBER_OPTIONS, title: 'Owners' },
  follower: { ...MEMBER_OPTIONS, title: 'Followers' },
  status: {
    title: 'Status',
    queryKey: 'task_status.options',
    multiple: true,
    allowed_operators: ['is', 'is any of', 'is not'],
    icon: 'status-mono',
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: statuses.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    }
  },
  priority: {
    title: 'Priority',
    queryKey: 'task_priority.options',
    multiple: true,
    allowed_operators: ['is', 'is any of', 'is not'],
    icon: 'list-unordered-mono',
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: priorities.filter((priority) =>
          priority.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    }
  }
};

export const getChangedTaskFields = (initialTask, shapedTaskObject) => {
  const changedFields = {};
  Object.keys(shapedTaskObject).forEach((key) => {
    if (initialTask[key] !== shapedTaskObject[key]) {
      changedFields[key] = shapedTaskObject[key];
    }
  });
  return changedFields;
};

export const getChangedValues = (initialValues, currentValues) => {
  const changedValues = [];
  for (let i = 0; i < initialValues.length; i++) {
    const initialValue = initialValues[i];

    if (!currentValues.includes(initialValue)) {
      changedValues.push(initialValue);
    }
  }

  return changedValues;
};

export const changedRequester = (initialValue, currentValue) => {
  if (initialValue !== currentValue) {
    return currentValue;
  }
  return null;
};
